// weekly_planner.ts - Інтерактивний планувальник тижневого графіка з перетягуванням та смарт-пошуком слотів

export interface PlannerTask {
    id: string;
    dayIndex: number; // 0=Пн, 1=Вт, 2=Ср, 3=Чт, 4=Пт, 5=Сб, 6=Нд, -1=Пул боргу
    startTime: string; // '18:30'
    endTime: string; // '22:30'
    durationMins: number; // 240
    title: string;
    category: 'delivery' | 'school' | 'pum' | 'sleep' | 'commute' | 'meal' | 'routine' | 'sport' | 'other';
    isFixed: boolean; // Фіксована задача (не можна накладати або рухати авто)
    allowSplit: boolean; // Чи дозволено розділяти
    minStart?: string;
    maxEnd?: string;
    priority: 'high' | 'medium' | 'low';
    status: 'planned' | 'in_progress' | 'completed' | 'missed' | 'needs_reschedule';
    notes?: string;
    weatherAlert?: string;
}

export interface FreeSlot {
    dayIndex: number;
    startMins: number;
    endMins: number;
    startTime: string;
    endTime: string;
    durationMins: number;
}

export interface CandidateSlot {
    dayIndex: number;
    dayName: string;
    startTime: string;
    endTime: string;
    durationMins: number;
    isPeak: boolean;
    isBuffer: boolean;
    gapDurationMins: number;
    description: string;
}

const DAYS_UK = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', "П'ятниця", 'Субота', 'Неділя'];
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

// Налаштування пікових годин
let plannerSettings = {
    saturdayPeakStart: '17:00',
    saturdayPeakEnd: '22:00',
    deliveryWeeklyQuota: 25.0
};

// Стан планувальника
let tasks: PlannerTask[] = [];
let draggedTaskId: string | null = null;
let activeWeatherAlert: { dayIndex: number; text: string; taskTitle: string } | null = {
    dayIndex: 0,
    text: 'Очікується злива та шквал вітру. Доставка на двоколісному під ризиком.',
    taskTitle: 'Доставка (Glovo + Bolt)'
};

// Часові утиліти
function timeToMins(timeStr: string): number {
    if (!timeStr) return 0;
    if (timeStr === '24:00' || timeStr === '00:00_end') return 1440;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function minsToTime(mins: number): string {
    const clamped = Math.max(0, Math.min(1440, mins));
    if (clamped === 1440) return '24:00';
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDuration(mins: number): string {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h} год ${m} хв`;
    if (h > 0) return `${h} год`;
    return `${m} хв`;
}

// Базовий графік-шаблон
export function createDefaultSchedule(): PlannerTask[] {
    const result: PlannerTask[] = [];

    // Понеділок - П'ятниця
    for (let day = 0; day < 5; day++) {
        result.push(
            {
                id: `sleep_${day}`,
                dayIndex: day,
                startTime: '00:00',
                endTime: '09:00',
                durationMins: 540,
                title: 'Сон (Залізне вікно)',
                category: 'sleep',
                isFixed: true,
                allowSplit: false,
                priority: 'high',
                status: 'planned'
            },
            {
                id: `routine_morning_${day}`,
                dayIndex: day,
                startTime: '09:00',
                endTime: '10:30',
                durationMins: 90,
                title: 'Ранок, побут & сніданок',
                category: 'routine',
                isFixed: false,
                allowSplit: false,
                priority: 'medium',
                status: 'planned'
            },
            {
                id: `commute_pum_${day}`,
                dayIndex: day,
                startTime: '10:30',
                endTime: '11:00',
                durationMins: 30,
                title: 'Дорога до ПУМ',
                category: 'commute',
                isFixed: false,
                allowSplit: false,
                priority: 'medium',
                status: 'planned'
            },
            {
                id: `pum_${day}`,
                dayIndex: day,
                startTime: '11:00',
                endTime: '12:30',
                durationMins: 90,
                title: 'ПУМ (технік)',
                category: 'pum',
                isFixed: true,
                allowSplit: false,
                priority: 'high',
                status: 'planned'
            },
            {
                id: `lunch_${day}`,
                dayIndex: day,
                startTime: '12:30',
                endTime: '13:00',
                durationMins: 30,
                title: 'Обід & перепочинок',
                category: 'meal',
                isFixed: false,
                allowSplit: false,
                priority: 'low',
                status: 'planned'
            },
            {
                id: `commute_school_${day}`,
                dayIndex: day,
                startTime: '13:00',
                endTime: '13:30',
                durationMins: 30,
                title: 'Дорога до ліцею',
                category: 'commute',
                isFixed: false,
                allowSplit: false,
                priority: 'medium',
                status: 'planned'
            },
            {
                id: `school_${day}`,
                dayIndex: day,
                startTime: '13:30',
                endTime: '18:00',
                durationMins: 270,
                title: 'Школа / Ліцей (інклюзія)',
                category: 'school',
                isFixed: true,
                allowSplit: false,
                priority: 'high',
                status: 'planned'
            },
            {
                id: `commute_delivery_${day}`,
                dayIndex: day,
                startTime: '18:00',
                endTime: '18:30',
                durationMins: 30,
                title: 'Перехід на точку замовлень',
                category: 'commute',
                isFixed: false,
                allowSplit: false,
                priority: 'medium',
                status: 'planned'
            },
            {
                id: `delivery_${day}`,
                dayIndex: day,
                startTime: '18:30',
                endTime: '22:30',
                durationMins: 240,
                title: 'Доставка (Glovo + Bolt)',
                category: 'delivery',
                isFixed: false,
                allowSplit: false,
                minStart: '17:00',
                maxEnd: '23:00',
                priority: 'medium',
                status: 'planned'
            },
            {
                id: `dinner_evening_${day}`,
                dayIndex: day,
                startTime: '22:30',
                endTime: '24:00',
                durationMins: 90,
                title: 'Вечеря, душ, особистий час',
                category: 'routine',
                isFixed: false,
                allowSplit: false,
                priority: 'medium',
                status: 'planned'
            }
        );
    }

    // Субота (dayIndex = 5)
    result.push(
        {
            id: 'sleep_sat',
            dayIndex: 5,
            startTime: '00:00',
            endTime: '09:00',
            durationMins: 540,
            title: 'Сон (Залізне вікно)',
            category: 'sleep',
            isFixed: true,
            allowSplit: false,
            priority: 'high',
            status: 'planned'
        },
        // 09:00 - 17:00 Вільно (8 годин для відпочинку/побуту або резерву)
        {
            id: 'delivery_sat',
            dayIndex: 5,
            startTime: '17:00',
            endTime: '22:00',
            durationMins: 300, // 5 годин обов'язковий пік
            title: 'Доставка (Glovo + Bolt)',
            category: 'delivery',
            isFixed: false,
            allowSplit: false,
            minStart: '12:00',
            maxEnd: '23:00',
            priority: 'medium',
            status: 'planned',
            notes: 'Суботній піковий блок замовлень (5 год)'
        },
        {
            id: 'dinner_sat',
            dayIndex: 5,
            startTime: '22:00',
            endTime: '24:00',
            durationMins: 120,
            title: 'Вечеря, відпочинок, вільний час',
            category: 'routine',
            isFixed: false,
            allowSplit: false,
            priority: 'low',
            status: 'planned'
        }
    );

    // Неділя (dayIndex = 6) - Повний вихідний буфер
    result.push({
        id: 'sleep_sun',
        dayIndex: 6,
        startTime: '00:00',
        endTime: '09:00',
        durationMins: 540,
        title: 'Сон (Залізне вікно)',
        category: 'sleep',
        isFixed: true,
        allowSplit: false,
        priority: 'high',
        status: 'planned'
    });
    // Решта дня 09:00 - 24:00 повністю вільна (буфер відновлення)

    return result;
}

// Розрахунок неперервних вільних слотів для заданого дня
export function getFreeSlotsForDay(dayIndex: number): FreeSlot[] {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIndex)
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

    const freeSlots: FreeSlot[] = [];
    let currentMins = 0;

    for (const task of dayTasks) {
        const taskStart = timeToMins(task.startTime);
        const taskEnd = timeToMins(task.endTime);

        if (taskStart > currentMins) {
            const gap = taskStart - currentMins;
            if (gap >= 15) { // Мінімальний проміжок 15 хвилин
                freeSlots.push({
                    dayIndex,
                    startMins: currentMins,
                    endMins: taskStart,
                    startTime: minsToTime(currentMins),
                    endTime: minsToTime(taskStart),
                    durationMins: gap
                });
            }
        }
        currentMins = Math.max(currentMins, taskEnd);
    }

    if (currentMins < 1440) {
        const gap = 1440 - currentMins;
        if (gap >= 15) {
            freeSlots.push({
                dayIndex,
                startMins: currentMins,
                endMins: 1440,
                startTime: minsToTime(currentMins),
                endTime: '24:00',
                durationMins: gap
            });
        }
    }

    return freeSlots;
}

// Перевірка можливості розміщення задачі в заданий день і час без накладання
export function canPlaceTask(
    dayIndex: number,
    startMins: number,
    durationMins: number,
    excludeTaskId: string | null = null
): { ok: boolean; reason?: string } {
    const endMins = startMins + durationMins;
    if (endMins > 1440) {
        return { ok: false, reason: 'Задача виходить за межі поточної доби (24:00)' };
    }

    // Залізне вікно сну недоторканне (00:00 - 09:00)
    if (startMins < 540 && durationMins < 540) { // Якщо це не сам сон
        // Перевіряємо чи перетинається з вікном сну
        if (startMins < 540) {
            return { ok: false, reason: 'Неможливо поставити в залізне вікно сну (00:00 – 09:00)' };
        }
    }

    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex && t.id !== excludeTaskId);

    for (const other of dayTasks) {
        const otherStart = timeToMins(other.startTime);
        const otherEnd = timeToMins(other.endTime);

        // Перевірка на перекриття
        if (startMins < otherEnd && endMins > otherStart) {
            return {
                ok: false,
                reason: `Конфлікт із задачею «${other.title}» (${other.startTime}–${other.endTime})`
            };
        }
    }

    return { ok: true };
}

// Розумний алгоритм пошуку доступних слотів для задачі
export function findAvailableSlots(task: PlannerTask): CandidateSlot[] {
    const candidates: CandidateSlot[] = [];
    const duration = task.durationMins;
    const taskMinStartMins = task.minStart ? timeToMins(task.minStart) : 0;
    const taskMaxEndMins = task.maxEnd ? timeToMins(task.maxEnd) : 1440;

    // Шукаємо по всіх 7 днях
    for (let day = 0; day < 7; day++) {
        // Якщо це поточний день задачі і вона вже призначена, шукаємо в інших днях або в інший час
        const freeSlots = getFreeSlotsForDay(day);

        for (const slot of freeSlots) {
            // Вікно сну ігноруємо
            let usableStart = Math.max(slot.startMins, 540); // Від 09:00
            let usableEnd = slot.endMins;

            if (usableEnd - usableStart >= duration) {
                // Якщо субота і доставка, пріоритезуємо піковий блок або денний блок
                if (day === 5 && task.category === 'delivery') {
                    // Перевіряємо блок з 10:00
                    if (slot.startMins <= 600 && slot.endMins >= 600 + duration) {
                        candidates.push({
                            dayIndex: day,
                            dayName: DAYS_UK[day],
                            startTime: '10:00',
                            endTime: minsToTime(600 + duration),
                            durationMins: duration,
                            isPeak: false,
                            isBuffer: false,
                            gapDurationMins: slot.durationMins,
                            description: 'Денний вільний проміжок перед вечірнім піком'
                        });
                    }
                    // Перевіряємо блок з 12:00
                    if (slot.startMins <= 720 && slot.endMins >= 720 + duration) {
                        candidates.push({
                            dayIndex: day,
                            dayName: DAYS_UK[day],
                            startTime: '12:00',
                            endTime: minsToTime(720 + duration),
                            durationMins: duration,
                            isPeak: false,
                            isBuffer: false,
                            gapDurationMins: slot.durationMins,
                            description: 'Денний слот доставки'
                        });
                    }
                    // Перевіряємо вечірній піковий блок (17:00) якщо вільно
                    const peakStartMins = timeToMins(plannerSettings.saturdayPeakStart);
                    if (slot.startMins <= peakStartMins && slot.endMins >= peakStartMins + duration) {
                        candidates.push({
                            dayIndex: day,
                            dayName: DAYS_UK[day],
                            startTime: plannerSettings.saturdayPeakStart,
                            endTime: minsToTime(peakStartMins + duration),
                            durationMins: duration,
                            isPeak: true,
                            isBuffer: false,
                            gapDurationMins: slot.durationMins,
                            description: '⭐ Пікові години високого заробітку Glovo/Bolt'
                        });
                    }
                } else if (day === 6) {
                    // Неділя - буфер відновлення
                    candidates.push({
                        dayIndex: day,
                        dayName: DAYS_UK[day],
                        startTime: minsToTime(usableStart),
                        endTime: minsToTime(usableStart + duration),
                        durationMins: duration,
                        isPeak: false,
                        isBuffer: true,
                        gapDurationMins: slot.durationMins,
                        description: '🌿 Використання недільного буфера відновлення'
                    });
                } else {
                    // Будні
                    // Початок прямо в слоті
                    const check1 = canPlaceTask(day, usableStart, duration, task.id);
                    if (check1.ok) {
                        candidates.push({
                            dayIndex: day,
                            dayName: DAYS_UK[day],
                            startTime: minsToTime(usableStart),
                            endTime: minsToTime(usableStart + duration),
                            durationMins: duration,
                            isPeak: false,
                            isBuffer: false,
                            gapDurationMins: slot.durationMins,
                            description: `Вільне вікно на ${formatDuration(slot.durationMins)}`
                        });
                    }
                }
            }
        }
    }

    // Прибираємо дублікати
    const seen = new Set<string>();
    return candidates.filter(c => {
        const key = `${c.dayIndex}_${c.startTime}_${c.endTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// Завантаження графіка
export async function loadScheduleData() {
    try {
        const res = await fetch('api/slots.php?action=get_schedule');
        if (res.ok) {
            const data = await res.json();
            if (data.success && data.schedule && Array.isArray(data.schedule.tasks)) {
                tasks = data.schedule.tasks;
                if (data.schedule.settings) {
                    plannerSettings = { ...plannerSettings, ...data.schedule.settings };
                }
                renderWeeklyPlanner();
                return;
            }
        }
    } catch (e) {
        console.warn('API get_schedule failed, using local storage or template', e);
    }

    // Fallback до LocalStorage
    const saved = localStorage.getItem('syncflow_weekly_schedule_v2');
    if (saved) {
        try {
            const parsed = jsonParseSafe(saved);
            if (parsed && Array.isArray(parsed.tasks)) {
                tasks = parsed.tasks;
                renderWeeklyPlanner();
                return;
            }
        } catch (e) {}
    }

    // Якщо нічого немає - застосовуємо базовий графік
    tasks = createDefaultSchedule();
    saveScheduleDataLocally();
    renderWeeklyPlanner();
}

function jsonParseSafe(str: string): any {
    try {
        return JSON.parse(str);
    } catch (e) {
        return null;
    }
}

// Збереження графіка
export async function saveScheduleData() {
    saveScheduleDataLocally();

    try {
        await fetch('api/slots.php?action=save_schedule', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                schedule: {
                    tasks,
                    settings: plannerSettings,
                    updated_at: new Date().toISOString()
                }
            })
        });
    } catch (e) {
        console.error('Failed to sync schedule with server', e);
    }
}

function saveScheduleDataLocally() {
    localStorage.setItem(
        'syncflow_weekly_schedule_v2',
        JSON.stringify({
            tasks,
            settings: plannerSettings
        })
    );
}

// Підрахунок денної статистики
function getDayStats(dayIndex: number) {
    const dayTasks = tasks.filter(t => t.dayIndex === dayIndex);
    let workMins = 0;
    let commuteMins = 0;
    let routineMins = 0;
    let sleepMins = 0;

    for (const t of dayTasks) {
        if (t.category === 'school' || t.category === 'pum' || t.category === 'delivery') {
            workMins += t.durationMins;
        } else if (t.category === 'commute') {
            commuteMins += t.durationMins;
        } else if (t.category === 'routine' || t.category === 'meal' || t.category === 'sport') {
            routineMins += t.durationMins;
        } else if (t.category === 'sleep') {
            sleepMins += t.durationMins;
        }
    }

    const scheduledMins = workMins + commuteMins + routineMins + sleepMins;
    const freeMins = Math.max(0, 1440 - scheduledMins);

    return {
        workHours: (workMins / 60).toFixed(1),
        commuteHours: (commuteMins / 60).toFixed(1),
        routineHours: (routineMins / 60).toFixed(1),
        sleepHours: (sleepMins / 60).toFixed(1),
        freeHours: (freeMins / 60).toFixed(1),
        scheduledHours: (scheduledMins / 60).toFixed(1)
    };
}

// Підрахунок тижневої доставки
function getWeekDeliverySummary() {
    const deliveryTasks = tasks.filter(t => t.category === 'delivery');
    let plannedHours = 0;
    let completedHours = 0;
    let debtHours = 0;

    for (const t of deliveryTasks) {
        const hours = t.durationMins / 60;
        if (t.dayIndex === -1 || t.status === 'needs_reschedule' || t.status === 'missed') {
            debtHours += hours;
        } else if (t.status === 'completed') {
            completedHours += hours;
            plannedHours += hours;
        } else {
            plannedHours += hours;
        }
    }

    return {
        planned: plannedHours,
        completed: completedHours,
        debt: debtHours,
        quota: plannerSettings.deliveryWeeklyQuota
    };
}

// Головний рендер інтерфейсу планувальника
export function renderWeeklyPlanner() {
    const container = document.getElementById('weekly-planner-root');
    if (!container) return;

    const unassignedTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');
    const deliveryStats = getWeekDeliverySummary();
    const todayDayIndex = (new Date().getDay() + 6) % 7; // 0=Mon, 6=Sun

    let html = `
        <div class="weekly-planner-container">
            <!-- Верхня панель управління та фільтрів -->
            <div class="wp-control-bar">
                <div class="wp-control-left">
                    <span class="wp-view-title">
                        <i class="ri-calendar-event-line" style="color:var(--brand-orange)"></i>
                        Тижневий Планувальник Слотів
                    </span>
                    <button class="wp-btn" onclick="applyBaseScheduleTemplate()" data-tooltip="Скинути або заповнити тиждень базовим шаблоном" data-tooltip-pos="bottom">
                        <i class="ri-history-line"></i> Застосувати шаблон
                    </button>
                    <button class="wp-btn" onclick="openPeakSettingsModal()" data-tooltip="Налаштування пікових годин суботи" data-tooltip-pos="bottom">
                        <i class="ri-time-line"></i> Пік доставки (${plannerSettings.saturdayPeakStart}–${plannerSettings.saturdayPeakEnd})
                    </button>
                    <span class="wp-debt-badge ${unassignedTasks.length > 0 ? 'has-debt' : ''}" data-tooltip="${unassignedTasks.length > 0 ? 'Є нерозподілені задачі, що потребують перенесення' : 'Всі задачі успішно заплановані'}">
                        <i class="${unassignedTasks.length > 0 ? 'ri-alarm-warning-fill' : 'ri-checkbox-circle-fill'}"></i>
                        <span>${unassignedTasks.length > 0 ? `Борг: ${unassignedTasks.length}` : 'Боргів немає'}</span>
                    </span>
                </div>
                <div class="wp-control-right">
                    <button class="wp-btn wp-btn-primary" onclick="openAddTaskModal()">
                        <i class="ri-add-line"></i> Додати задачу
                    </button>
                    <button class="wp-btn" onclick="autoRescheduleDebtTasks()" data-tooltip="Автоматично знайти слоти для всіх перенесень" data-tooltip-pos="bottom">
                        <i class="ri-magic-line"></i> Перепланувати день
                    </button>
                </div>
            </div>

            <!-- Попередження погоди (якщо є) -->
            ${
                activeWeatherAlert
                    ? `
                <div class="wp-alert-banner alert-rain">
                    <div class="wp-alert-content">
                        <i class="ri-rainy-line"></i>
                        <span><strong>Погода на ${DAYS_UK[activeWeatherAlert.dayIndex]}:</strong> ${activeWeatherAlert.text}</span>
                    </div>
                    <button class="wp-btn wp-btn-danger" onclick="rescheduleWeatherTask(${activeWeatherAlert.dayIndex})">
                        <i class="ri-arrow-right-line"></i> Перенести доставку
                    </button>
                </div>
            `
                    : ''
            }

            <!-- Пул боргу (відображається ТІЛЬКИ коли реально є задачі для перенесення) -->
            ${
                unassignedTasks.length > 0
                    ? `
            <div class="wp-debt-pool has-items">
                <div class="wp-debt-header">
                    <span class="wp-debt-title">
                        <i class="ri-alarm-warning-fill"></i>
                        Потребують перенесення / Борг задач (${unassignedTasks.length})
                    </span>
                    <span style="font-size:12px;color:var(--text-muted)">
                        Перетягніть картку на потрібний день або натисніть «Знайти слот»
                    </span>
                </div>
                <div class="wp-debt-cards-list">
                    ${unassignedTasks.map(t => renderDebtCard(t)).join('')}
                </div>
            </div>
            `
                    : ''
            }

            <!-- Сітка 7 днів тижня -->
            <div class="wp-week-grid" id="wp-week-grid">
                ${DAYS_UK.map((dayName, dayIdx) => renderDayColumn(dayIdx, dayName, todayDayIndex === dayIdx)).join('')}
            </div>
        </div>
    `;

    container.innerHTML = html;

    // Ініціалізація Drag and Drop слухачів
    setupDragAndDrop();
}

function renderDebtCard(task: PlannerTask): string {
    return `
        <div class="wp-task-card wp-task-${task.category} status-needs-reschedule" 
             draggable="true" 
             data-task-id="${task.id}">
            <div class="wp-card-top">
                <span class="wp-card-time"><i class="ri-alarm-warning-line"></i> Борг</span>
                <span class="wp-card-duration">${formatDuration(task.durationMins)}</span>
            </div>
            <div class="wp-card-title">
                ${getCategoryIcon(task.category)} ${task.title}
            </div>
            <div class="wp-card-actions">
                <button class="wp-action-btn btn-reschedule" onclick="openFindSlotModal('${task.id}')" data-tooltip="Підібрати вільний слот" data-tooltip-pos="top">
                    <i class="ri-search-eye-line"></i> Знайти слот
                </button>
                ${
                    task.allowSplit || task.durationMins >= 120
                        ? `
                    <button class="wp-action-btn" onclick="openSplitTaskModal('${task.id}')" data-tooltip="Розділити задачу" data-tooltip-pos="top">
                        <i class="ri-scissors-cut-line"></i> Розділити
                    </button>
                `
                        : ''
                }
                <button class="wp-action-btn" onclick="dismissDebtTask('${task.id}')" data-tooltip="Скасувати / Видалити" data-tooltip-pos="top">
                    <i class="ri-close-line"></i>
                </button>
            </div>
        </div>
    `;
}

function renderDayColumn(dayIdx: number, dayName: string, isToday: boolean): string {
    const dayTasks = tasks
        .filter(t => t.dayIndex === dayIdx && t.status !== 'needs_reschedule')
        .sort((a, b) => timeToMins(a.startTime) - timeToMins(b.startTime));

    const freeSlots = getFreeSlotsForDay(dayIdx);
    const stats = getDayStats(dayIdx);

    // Збираємо потік елементів (задачі + вільні слоти) хронологічно
    type TimelineItem =
        | { type: 'task'; data: PlannerTask; startMins: number }
        | { type: 'free'; data: FreeSlot; startMins: number };

    const items: TimelineItem[] = [];
    dayTasks.forEach(t => items.push({ type: 'task', data: t, startMins: timeToMins(t.startTime) }));
    freeSlots.forEach(f => items.push({ type: 'free', data: f, startMins: f.startMins }));
    items.sort((a, b) => a.startMins - b.startMins);

    return `
        <div class="wp-day-column ${isToday ? 'is-today' : ''}" data-day-index="${dayIdx}" id="wp-day-col-${dayIdx}">
            <div class="wp-day-header">
                <div class="wp-day-name-row">
                    <span class="wp-day-name">${dayName}</span>
                    ${isToday ? `<span class="wp-day-badge-today">Сьогодні</span>` : ''}
                    ${dayIdx === 5 ? `<span class="wp-day-badge-peak">5 год пік</span>` : ''}
                    ${dayIdx === 6 ? `<span class="wp-day-badge-weekend">Вихідний</span>` : ''}
                </div>
                <div class="wp-day-hours-summary">
                    <span>План: <strong>${stats.scheduledHours}</strong> / 24 год</span>
                    <span>Вільно: <strong style="color:#34d399">${stats.freeHours} год</strong></span>
                </div>
            </div>

            <div class="wp-day-body" data-day-drop-zone="${dayIdx}">
                ${items
                    .map(it => {
                        if (it.type === 'task') {
                            return renderTaskCard(it.data);
                        } else {
                            return renderFreeSlotItem(it.data);
                        }
                    })
                    .join('')}
            </div>

            <div class="wp-day-footer">
                <div class="wp-footer-stat-row">
                    <span>💼 Робота: <strong>${stats.workHours} год</strong></span>
                    <span>😴 Сон: <strong>${stats.sleepHours} год</strong></span>
                </div>
                <div class="wp-footer-stat-row">
                    <span>🚗 Дорога: <strong>${stats.commuteHours} год</strong></span>
                    <span>🏠 Побут: <strong>${stats.routineHours} год</strong></span>
                </div>
            </div>
        </div>
    `;
}

function renderTaskCard(task: PlannerTask): string {
    const isCompleted = task.status === 'completed';
    const isFixed = task.isFixed;

    return `
        <div class="wp-task-card wp-task-${task.category} ${isCompleted ? 'status-completed' : ''} ${isFixed ? 'is-fixed' : ''}"
             draggable="${!isFixed}" 
             data-task-id="${task.id}"
             id="task-card-${task.id}">
            <div class="wp-card-top">
                <span class="wp-card-time">
                    <i class="ri-time-line"></i> ${task.startTime}–${task.endTime}
                </span>
                <div class="wp-card-badges">
                    <span class="wp-card-duration">${formatDuration(task.durationMins)}</span>
                    ${isFixed ? `<i class="ri-lock-line wp-badge-icon" data-tooltip="Фіксована задача" data-tooltip-pos="top"></i>` : ''}
                </div>
            </div>
            <div class="wp-card-title">
                ${getCategoryIcon(task.category)} ${task.title}
            </div>
            <div class="wp-card-actions">
                ${
                    !isFixed
                        ? `
                    <button class="wp-action-btn ${isCompleted ? 'btn-complete' : ''}" 
                            onclick="toggleTaskCompleted('${task.id}')" 
                            data-tooltip="${isCompleted ? 'Відновити' : 'Відмітити виконаним'}" 
                            data-tooltip-pos="top">
                        <i class="${isCompleted ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}"></i>
                    </button>
                    <button class="wp-action-btn btn-reschedule" 
                            onclick="openFindSlotModal('${task.id}')" 
                            data-tooltip="Знайти слот для перенесення" 
                            data-tooltip-pos="top">
                        <i class="ri-search-eye-line"></i>
                    </button>
                    <button class="wp-action-btn" 
                            onclick="moveTaskToDebt('${task.id}')" 
                            data-tooltip="Позначити пропущеним / у борг" 
                            data-tooltip-pos="top">
                        <i class="ri-alarm-warning-line"></i>
                    </button>
                `
                        : `
                    <span style="font-size:10px;color:var(--text-muted);"><i class="ri-lock-2-line"></i> Фіксовано</span>
                `
                }
            </div>
        </div>
    `;
}

function renderFreeSlotItem(slot: FreeSlot): string {
    return `
        <div class="wp-free-slot" 
             onclick="openQuickAddModal(${slot.dayIndex}, '${slot.startTime}', ${slot.durationMins})"
             data-tooltip="Вільне вікно на ${formatDuration(slot.durationMins)}. Натисніть, щоб додати задачу"
             data-tooltip-pos="top">
            <div class="wp-free-slot-left">
                <i class="ri-add-circle-line"></i>
                <span>Вільно — ${formatDuration(slot.durationMins)}</span>
            </div>
            <span class="wp-free-slot-time">${slot.startTime}–${slot.endTime}</span>
        </div>
    `;
}

function getCategoryIcon(cat: string): string {
    switch (cat) {
        case 'delivery':
            return '<i class="ri-riding-line" style="color:var(--brand-orange)"></i>';
        case 'school':
            return '<i class="ri-book-read-line" style="color:#f59e0b"></i>';
        case 'pum':
            return '<i class="ri-macbook-line" style="color:#0ea5e9"></i>';
        case 'sleep':
            return '<i class="ri-moon-clear-line" style="color:#8b5cf6"></i>';
        case 'commute':
            return '<i class="ri-walk-line" style="color:#64748b"></i>';
        case 'meal':
            return '<i class="ri-restaurant-line" style="color:#10b981"></i>';
        case 'routine':
            return '<i class="ri-home-4-line" style="color:#3b82f6"></i>';
        case 'sport':
            return '<i class="ri-run-line" style="color:#a855f7"></i>';
        default:
            return '<i class="ri-task-line"></i>';
    }
}

// Налаштування Drag & Drop
function setupDragAndDrop() {
    const cards = document.querySelectorAll('.wp-task-card[draggable="true"]');
    cards.forEach(card => {
        card.addEventListener('dragstart', (e: any) => {
            const taskId = card.getAttribute('data-task-id');
            if (taskId) {
                draggedTaskId = taskId;
                card.classList.add('is-dragging');
                e.dataTransfer.setData('text/plain', taskId);
                e.dataTransfer.effectAllowed = 'move';
            }
        });

        card.addEventListener('dragend', () => {
            card.classList.remove('is-dragging');
            draggedTaskId = null;
            clearDropzoneStates();
        });
    });

    const columns = document.querySelectorAll('.wp-day-column');
    columns.forEach(col => {
        col.addEventListener('dragover', (e: any) => {
            e.preventDefault();
            if (!draggedTaskId) return;

            const dayIdx = Number(col.getAttribute('data-day-index'));
            const task = tasks.find(t => t.id === draggedTaskId);
            if (!task) return;

            // Перевіряємо чи є хоча б один сумісний вільний слот
            const freeSlots = getFreeSlotsForDay(dayIdx);
            const canFit = freeSlots.some(s => s.durationMins >= task.durationMins);

            if (canFit) {
                col.classList.add('dropzone-hover-valid');
                col.classList.remove('dropzone-hover-invalid');
                e.dataTransfer.dropEffect = 'move';
            } else {
                col.classList.add('dropzone-hover-invalid');
                col.classList.remove('dropzone-hover-valid');
                e.dataTransfer.dropEffect = 'none';
            }
        });

        col.addEventListener('dragleave', (e: any) => {
            if (!col.contains(e.relatedTarget)) {
                col.classList.remove('dropzone-hover-valid', 'dropzone-hover-invalid');
            }
        });

        col.addEventListener('drop', (e: any) => {
            e.preventDefault();
            clearDropzoneStates();
            const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
            if (!taskId) return;

            const dayIdx = Number(col.getAttribute('data-day-index'));
            handleDropTaskOnDay(taskId, dayIdx);
        });
    });
}

function clearDropzoneStates() {
    document.querySelectorAll('.wp-day-column').forEach(c => {
        c.classList.remove('dropzone-hover-valid', 'dropzone-hover-invalid');
    });
}

// Обробка розміщення задачі при Drop на день
function handleDropTaskOnDay(taskId: string, dayIndex: number) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    if (task.isFixed) {
        showPlannerToast('Фіксовані задачі не можна переміщувати', 'warning');
        return;
    }

    const freeSlots = getFreeSlotsForDay(dayIndex);
    // Шукаємо перший відповідний вільний слот
    const suitableSlot = freeSlots.find(s => s.durationMins >= task.durationMins);

    if (!suitableSlot) {
        showPlannerToast(
            `У день ${DAYS_UK[dayIndex]} немає неперервного вікна на ${formatDuration(task.durationMins)}. Спробуйте інший день.`,
            'error'
        );
        return;
    }

    // Встановлюємо новий час початку та завершення
    const newStartMins = suitableSlot.startMins;
    const newEndMins = newStartMins + task.durationMins;

    task.dayIndex = dayIndex;
    task.startTime = minsToTime(newStartMins);
    task.endTime = minsToTime(newEndMins);
    task.status = 'planned';

    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Задачу «${task.title}» перенесено на ${DAYS_UK[dayIndex]} (${task.startTime}–${task.endTime})`, 'success');
}

// Відмітити виконання
(window as any).toggleTaskCompleted = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    task.status = task.status === 'completed' ? 'planned' : 'completed';
    saveScheduleData();
    renderWeeklyPlanner();
};

// Перемістити задачу в борг (не виконано)
(window as any).moveTaskToDebt = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    task.dayIndex = -1;
    task.status = 'needs_reschedule';
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`«${task.title}» переведено в борг. Виберіть новий слот для перенесення.`, 'info');
};

// Відхилити задачу з боргу
(window as any).dismissDebtTask = function(taskId: string) {
    tasks = tasks.filter(t => t.id !== taskId);
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Задачу видалено з боргу', 'info');
};

// Застосувати базовий графік
(window as any).applyBaseScheduleTemplate = function() {
    if (confirm('Скинути поточний тиждень та застосувати базовий шаблон графіка?')) {
        tasks = createDefaultSchedule();
        saveScheduleData();
        renderWeeklyPlanner();
        showPlannerToast('Базовий шаблон тижня успішно застосовано!', 'success');
    }
};

// Відкрити модалку 🔎 «Знайти слот»
(window as any).openFindSlotModal = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const candidates = findAvailableSlots(task);

    const modalHtml = `
        <div class="wp-modal-overlay" id="wp-find-slot-modal">
            <div class="wp-modal-container">
                <div class="wp-modal-header">
                    <h3><i class="ri-search-eye-line" style="color:var(--brand-orange)"></i> Доступні слоти для «${task.title}»</h3>
                    <button class="wp-modal-close" onclick="closePlannerModal('wp-find-slot-modal')"><i class="ri-close-line"></i></button>
                </div>
                <div class="wp-modal-body">
                    <div style="font-size:13px;color:var(--text-muted);display:flex;justify-content:space-between;align-items:center;">
                        <span>Тривалість задачі: <strong>${formatDuration(task.durationMins)}</strong></span>
                        <span>Знайдено варіантів: <strong>${candidates.length}</strong></span>
                    </div>

                    ${
                        candidates.length === 0
                            ? `
                        <div style="text-align:center;padding:30px;color:#fca5a5;">
                            <i class="ri-error-warning-line" style="font-size:36px;display:block;margin-bottom:8px;"></i>
                            Не знайдено жодного неперервного вікна на ${formatDuration(task.durationMins)} без конфліктів.<br>
                            <span style="font-size:12px;color:var(--text-muted);margin-top:6px;display:block;">
                                Спробуйте розділити задачу або змінити день.
                            </span>
                        </div>
                    `
                            : candidates
                                  .map(
                                      c => `
                        <div class="wp-candidate-card ${c.isPeak ? 'is-peak' : ''}">
                            <div class="wp-candidate-info">
                                <span class="wp-candidate-day">
                                    ${c.dayName}
                                    ${c.isPeak ? `<span class="wp-tag wp-tag-peak">⭐ Піковий час доставки</span>` : ''}
                                    ${c.isBuffer ? `<span class="wp-tag wp-tag-buffer">🌿 Буфер відновлення</span>` : ''}
                                </span>
                                <span class="wp-candidate-time">${c.startTime} – ${c.endTime} (${formatDuration(c.durationMins)})</span>
                                <span style="font-size:11px;color:var(--text-muted);">${c.description}</span>
                            </div>
                            <button class="wp-btn wp-btn-primary" onclick="applyCandidateSlot('${task.id}', ${c.dayIndex}, '${c.startTime}', '${c.endTime}')">
                                Перенести сюди
                            </button>
                        </div>
                    `
                                  )
                                  .join('')
                    }
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
};

// Застосувати знайдений слот
(window as any).applyCandidateSlot = function(taskId: string, dayIndex: number, startTime: string, endTime: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    task.dayIndex = dayIndex;
    task.startTime = startTime;
    task.endTime = endTime;
    task.status = 'planned';

    closePlannerModal('wp-find-slot-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Задачу «${task.title}» успішно перенесено на ${DAYS_UK[dayIndex]} (${startTime}–${endTime})`, 'success');
};

// Розділити задачу
(window as any).openSplitTaskModal = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const half = Math.floor(task.durationMins / 2);

    const modalHtml = `
        <div class="wp-modal-overlay" id="wp-split-modal">
            <div class="wp-modal-container">
                <div class="wp-modal-header">
                    <h3><i class="ri-scissors-cut-line" style="color:var(--brand-orange)"></i> Розділити задачу «${task.title}»</h3>
                    <button class="wp-modal-close" onclick="closePlannerModal('wp-split-modal')"><i class="ri-close-line"></i></button>
                </div>
                <div class="wp-modal-body">
                    <p style="font-size:13px;color:var(--text-muted);">
                        Поточна тривалість: <strong>${formatDuration(task.durationMins)}</strong>.<br>
                        Розбийте задачу на 2 частини для легшого розміщення у вільних вікнах.
                    </p>
                    <div class="wp-split-row">
                        <div>
                            <label style="font-size:11px;color:var(--text-muted);">Частина 1 (хв):</label>
                            <input type="number" id="split-part-1" class="wp-split-input" value="${half}" step="30" min="30" max="${task.durationMins - 30}">
                        </div>
                        <div>
                            <label style="font-size:11px;color:var(--text-muted);">Частина 2 (хв):</label>
                            <input type="number" id="split-part-2" class="wp-split-input" value="${task.durationMins - half}" step="30" min="30" max="${task.durationMins - 30}">
                        </div>
                    </div>
                    <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:10px;">
                        <button class="wp-btn" onclick="closePlannerModal('wp-split-modal')">Скасувати</button>
                        <button class="wp-btn wp-btn-primary" onclick="confirmSplitTask('${task.id}')">Підтвердити розділення</button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
};

(window as any).confirmSplitTask = function(taskId: string) {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const p1 = Number((document.getElementById('split-part-1') as HTMLInputElement)?.value) || 120;
    const p2 = Number((document.getElementById('split-part-2') as HTMLInputElement)?.value) || (task.durationMins - p1);

    task.durationMins = p1;
    task.title = `${task.title} (Частина 1)`;
    task.status = 'needs_reschedule';
    task.dayIndex = -1;

    const newTask: PlannerTask = {
        ...task,
        id: `task_${Date.now()}`,
        title: `${task.title.replace(' (Частина 1)', '')} (Частина 2)`,
        durationMins: p2,
        dayIndex: -1,
        status: 'needs_reschedule'
    };

    tasks.push(newTask);
    closePlannerModal('wp-split-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Задачу розділено на 2 частини в пулі перенесень', 'success');
};

// Налаштування пікових годин
(window as any).openPeakSettingsModal = function() {
    const modalHtml = `
        <div class="wp-modal-overlay" id="wp-peak-modal">
            <div class="wp-modal-container">
                <div class="wp-modal-header">
                    <h3><i class="ri-settings-4-line" style="color:var(--brand-orange)"></i> Пікові години доставки (Субота)</h3>
                    <button class="wp-modal-close" onclick="closePlannerModal('wp-peak-modal')"><i class="ri-close-line"></i></button>
                </div>
                <div class="wp-modal-body">
                    <p style="font-size:13px;color:var(--text-muted);">
                        Система використовує цей інтервал як найбільш пріоритетний для 5-годинної суботньої доставки.
                    </p>
                    <div style="display:flex;gap:12px;">
                        <div style="flex:1;">
                            <label style="font-size:12px;color:var(--text-muted)">Початок піку:</label>
                            <input type="time" id="peak-start-input" class="wp-split-input" value="${plannerSettings.saturdayPeakStart}">
                        </div>
                        <div style="flex:1;">
                            <label style="font-size:12px;color:var(--text-muted)">Кінець піку:</label>
                            <input type="time" id="peak-end-input" class="wp-split-input" value="${plannerSettings.saturdayPeakEnd}">
                        </div>
                    </div>
                    <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:10px;">
                        <button class="wp-btn" onclick="closePlannerModal('wp-peak-modal')">Скасувати</button>
                        <button class="wp-btn wp-btn-primary" onclick="savePeakSettings()">Зберегти</button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
};

(window as any).savePeakSettings = function() {
    const s = (document.getElementById('peak-start-input') as HTMLInputElement)?.value || '17:00';
    const e = (document.getElementById('peak-end-input') as HTMLInputElement)?.value || '22:00';
    plannerSettings.saturdayPeakStart = s;
    plannerSettings.saturdayPeakEnd = e;

    closePlannerModal('wp-peak-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast('Пікові години оновлено!', 'success');
};

// Швидке перенесення доставки через погоду
(window as any).rescheduleWeatherTask = function(dayIndex: number) {
    const deliveryTask = tasks.find(t => t.dayIndex === dayIndex && t.category === 'delivery');
    if (!deliveryTask) {
        showPlannerToast('Доставку на цей день не знайдено', 'info');
        return;
    }

    deliveryTask.dayIndex = -1;
    deliveryTask.status = 'needs_reschedule';
    activeWeatherAlert = null;

    saveScheduleData();
    renderWeeklyPlanner();
    openFindSlotModal(deliveryTask.id);
    showPlannerToast('Доставку знято через погоду. Оберіть слот у суботу або інший день.', 'warning');
};

// Перепланувати борги автоматично
(window as any).autoRescheduleDebtTasks = function() {
    const debtTasks = tasks.filter(t => t.dayIndex === -1 || t.status === 'needs_reschedule');
    if (debtTasks.length === 0) {
        showPlannerToast('Всі задачі вже заплановані, перепланування не потрібне!', 'info');
        return;
    }

    let rescheduledCount = 0;
    for (const task of debtTasks) {
        const slots = findAvailableSlots(task);
        if (slots.length > 0) {
            const best = slots[0];
            task.dayIndex = best.dayIndex;
            task.startTime = best.startTime;
            task.endTime = best.endTime;
            task.status = 'planned';
            rescheduledCount++;
        }
    }

    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Автоматично переплановано ${rescheduledCount} задач!`, 'success');
};

// Швидке додавання задачі
(window as any).openQuickAddModal = function(dayIndex: number, defaultStart: string, maxMins: number) {
    const modalHtml = `
        <div class="wp-modal-overlay" id="wp-add-modal">
            <div class="wp-modal-container">
                <div class="wp-modal-header">
                    <h3><i class="ri-add-circle-line" style="color:var(--brand-orange)"></i> Додати задачу на ${DAYS_UK[dayIndex]}</h3>
                    <button class="wp-modal-close" onclick="closePlannerModal('wp-add-modal')"><i class="ri-close-line"></i></button>
                </div>
                <div class="wp-modal-body">
                    <div>
                        <label style="font-size:12px;color:var(--text-muted)">Назва задачі:</label>
                        <input type="text" id="add-task-title" class="wp-split-input" placeholder="наприклад Доставка, Спорт, Покупки" value="Доставка">
                    </div>
                    <div style="display:flex;gap:12px;">
                        <div style="flex:1;">
                            <label style="font-size:12px;color:var(--text-muted)">Початок:</label>
                            <input type="time" id="add-task-start" class="wp-split-input" value="${defaultStart}">
                        </div>
                        <div style="flex:1;">
                            <label style="font-size:12px;color:var(--text-muted)">Тривалість (хв):</label>
                            <input type="number" id="add-task-dur" class="wp-split-input" value="${Math.min(240, maxMins)}" step="30" max="${maxMins}">
                        </div>
                    </div>
                    <div>
                        <label style="font-size:12px;color:var(--text-muted)">Категорія:</label>
                        <select id="add-task-cat" class="wp-split-input" style="background:#18181b">
                            <option value="delivery">🚴 Доставка (Glovo + Bolt)</option>
                            <option value="routine">🏠 Побут / Особистий час</option>
                            <option value="sport">🏋️ Спорт / Тренування</option>
                            <option value="meal">🍽 Обід / Вечеря</option>
                            <option value="school">🏫 Школа / Ліцей</option>
                            <option value="pum">💼 ПУМ</option>
                            <option value="other">📋 Інше</option>
                        </select>
                    </div>
                    <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:10px;">
                        <button class="wp-btn" onclick="closePlannerModal('wp-add-modal')">Скасувати</button>
                        <button class="wp-btn wp-btn-primary" onclick="confirmAddTask(${dayIndex})">Додати в розклад</button>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
};

(window as any).openAddTaskModal = function() {
    (window as any).openQuickAddModal(0, '18:30', 240);
};

(window as any).confirmAddTask = function(dayIndex: number) {
    const title = (document.getElementById('add-task-title') as HTMLInputElement)?.value.trim() || 'Нова задача';
    const startTime = (document.getElementById('add-task-start') as HTMLInputElement)?.value || '18:30';
    const durMins = Number((document.getElementById('add-task-dur') as HTMLInputElement)?.value) || 60;
    const cat = ((document.getElementById('add-task-cat') as HTMLSelectElement)?.value || 'delivery') as any;

    const startMins = timeToMins(startTime);
    const endMins = startMins + durMins;
    const check = canPlaceTask(dayIndex, startMins, durMins);

    if (!check.ok) {
        showPlannerToast(`Неможливо додати задачу: ${check.reason}`, 'error');
        return;
    }

    const newTask: PlannerTask = {
        id: `task_${Date.now()}`,
        dayIndex,
        startTime,
        endTime: minsToTime(endMins),
        durationMins: durMins,
        title,
        category: cat,
        isFixed: false,
        allowSplit: cat !== 'delivery',
        priority: 'medium',
        status: 'planned'
    };

    tasks.push(newTask);
    closePlannerModal('wp-add-modal');
    saveScheduleData();
    renderWeeklyPlanner();
    showPlannerToast(`Задачу «${title}» додано на ${DAYS_UK[dayIndex]}!`, 'success');
};

(window as any).closePlannerModal = function(id: string) {
    const m = document.getElementById(id);
    if (m) m.remove();
};

function showPlannerToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    if ((window as any).showMessage) {
        (window as any).showMessage(message, type);
    } else {
        alert(message);
    }
}

(window as any).renderWeeklyPlanner = renderWeeklyPlanner;
(window as any).loadScheduleData = loadScheduleData;

// Ініціалізація при завантаженні сторінки
document.addEventListener('DOMContentLoaded', () => {
    loadScheduleData();
});
